import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  TrendingUp, Coins, Gift, Trophy, Heart, MessageCircle,
  Users, Flame, ArrowUpRight, ArrowDownRight, Activity,
} from "lucide-react";

const StatBox = ({ icon: Icon, label, value, color, sub }) => (
  <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
    <div className="flex items-center gap-2 mb-2">
      <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
    </div>
    <p className="font-heading font-black text-xl text-card-foreground">{value}</p>
    {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
  </div>
);

export default function StatsPage() {
  const { api } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/stats/me").then((res) => setStats(res.data)).catch(console.error);
  }, [api]);

  if (!stats) {
    return (
      <div className="flex justify-center py-20">
        <div className="w-6 h-6 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="py-6 space-y-5" data-testid="stats-page">
      <div>
        <h1 className="font-heading font-black text-2xl text-foreground">Mes Statistiques</h1>
        <p className="text-sm text-muted-foreground mt-1">Ton activite sur ReallyFan</p>
      </div>

      {/* Credits overview */}
      <div className="bg-gradient-to-br from-[#FF6B35]/20 to-[#7000FF]/20 border border-foreground/10 rounded-3xl p-5">
        <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Solde</p>
        <p className="font-heading font-black text-3xl text-foreground">{stats.credits.balance} <span className="text-lg text-muted-foreground">credits</span></p>
        <div className="flex items-center gap-4 mt-3">
          <div className="flex items-center gap-1">
            <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-xs text-emerald-400 font-mono-credits">+{stats.credits.total_earned}</span>
            <span className="text-[10px] text-muted-foreground">gagnes</span>
          </div>
          <div className="flex items-center gap-1">
            <ArrowDownRight className="w-3.5 h-3.5 text-red-400" />
            <span className="text-xs text-red-400 font-mono-credits">-{stats.credits.total_spent}</span>
            <span className="text-[10px] text-muted-foreground">depenses</span>
          </div>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3">
        <StatBox icon={TrendingUp} label="Mises" value={stats.bets.count} color="#FF6B35" sub={`${stats.bets.total_credits} credits investis`} />
        <StatBox icon={Gift} label="Recompenses" value={stats.claims.count} color="#7000FF" sub="echangees" />
        <StatBox icon={Trophy} label="Defis" value={stats.challenges.completed} color="#FFD700" sub={stats.challenges.pending > 0 ? `${stats.challenges.pending} en attente` : "completes"} />
        <StatBox icon={Users} label="Parrainages" value={stats.referrals.count} color="#FF8FA3" sub="reussis" />
        <StatBox icon={Heart} label="Likes" value={stats.social.likes_given} color="#FF6B35" sub="donnes" />
        <StatBox icon={MessageCircle} label="Commentaires" value={stats.social.comments} color="#7000FF" sub="publies" />
        <StatBox icon={Activity} label="Follows" value={stats.social.follows} color="#FF8FA3" sub="artistes suivis" />
        <StatBox icon={Flame} label="Streak" value={`${stats.streak.current}j`} color="#FF6B35" sub={`Record: ${stats.streak.best}j`} />
      </div>

      {/* Credit history */}
      <div className="bg-card border border-border rounded-3xl p-5 shadow-sm">
        <h2 className="font-heading font-bold text-sm text-card-foreground mb-3">Historique credits</h2>
        {stats.credits.history.length === 0 ? (
          <p className="text-xs text-muted-foreground text-center py-4">Aucune transaction</p>
        ) : (
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {stats.credits.history.map((tx, i) => (
              <div key={i} className="flex items-center justify-between p-2 rounded-xl bg-white border border-border">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${tx.amount > 0 ? "bg-emerald-50" : "bg-red-50"}`}>
                    {tx.amount > 0 ? (
                      <ArrowUpRight className="w-3 h-3 text-emerald-500" />
                    ) : (
                      <ArrowDownRight className="w-3 h-3 text-red-400" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] text-card-foreground truncate">{tx.description || tx.type}</p>
                    <p className="text-[9px] text-muted-foreground">{new Date(tx.created_at).toLocaleDateString("fr-FR")}</p>
                  </div>
                </div>
                <span className={`font-mono-credits font-bold text-xs shrink-0 ${tx.amount > 0 ? "text-emerald-500" : "text-red-400"}`}>
                  {tx.amount > 0 ? "+" : ""}{tx.amount}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
